import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { getAllAuditLogs ,getDoctorRecentPatientAuditLogs } from "../services/auditService";
import { useAuth } from "../contexts/AuthContext";
import { getDoctorAllPatientAuditLogs } from "../services/auditService";
import SuperAdminGuard from "../components/guards/SuperAdminGuard";

/* ---------- Types ---------- */
interface AuditLog {
  id: string;
  title: string;
  actorName?: string | null;
  targetName?: string | null;
  subtitle?: string; // fallback for old data
  status: boolean;
  createdAt: string;
}

/* ---------- Utils ---------- */
const timeAgo = (dateString: string) => {
  const now = Date.now();
  const past = new Date(dateString).getTime();
  const diff = Math.floor((now - past) / 1000);

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hour ago`;
  return `${Math.floor(diff / 86400)} day ago`;
};

const ITEMS_PER_PAGE = 10;

/* ---------- Pagination Helper ---------- */
const getPaginationPages = (
  currentPage: number,
  totalPages: number
) => {
  const pages: (number | string)[] = [];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }

  pages.push(1);

  if (currentPage > 3) pages.push("...");

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) pages.push(i);

  if (currentPage < totalPages - 2) pages.push("...");

  pages.push(totalPages);

  return pages;
};

/* ---------- Component ---------- */
const AuditLogs: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  /* ---------- Fetch Logs ---------- */
  const { user } = useAuth();

useEffect(() => {
  const fetchLogs = async () => {
    try {
      let data = [];

      // 🔐 Doctor → doctor-only logs
      if (user?.role === "DOCTOR") {
        data = await getDoctorAllPatientAuditLogs();
      } else {
        // 🔐 SuperAdmin → all logs
        data = await getAllAuditLogs();
      }

      setAuditLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load audit logs", err);
    } finally {
      setLoading(false);
    }
  };

  fetchLogs();
}, [user]);

  

  /* ---------- Search ---------- */
  const filteredLogs = auditLogs.filter((log) =>
    `${log.title} ${log.actorName ?? ""} ${log.targetName ?? ""} ${log.subtitle ?? ""}`
  .toLowerCase()
  .includes(searchTerm.toLowerCase())
  );

  /* ---------- Pagination ---------- */
  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedLogs = filteredLogs.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  /* ---------- Render ---------- */
  return (
   
      <div className="space-y-6 p-6">
        {/* Title */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Audit Activity
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            View and search all system activities
          </p>
        </div>

        {/* Search */}
        <div className="card">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by actor or action..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field pl-10"
            />
          </div>
        </div>

        {/* Table */}
        <div className="card overflow-x-auto">
          {loading ? (
            <p className="text-center text-gray-500 py-6">
              Loading audit logs…
            </p>
          ) : (
            <>
              <table className="min-w-full border border-gray-200 rounded-lg">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Actor
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Action
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Target
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      Time
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
  {paginatedLogs.map((log) => {
    const actor =
      log.actorName ||
      (log.subtitle ? log.subtitle.split(" • ")[0] : "System");

    const target =
      log.targetName ||
      (log.subtitle ? log.subtitle.split(" • ")[1] : null);

    return (
      <tr key={log.id} className="hover:bg-gray-50">
        <td className="px-4 py-3 text-sm text-gray-900">
          {actor || "System"}
        </td>

        <td className="px-4 py-3 text-sm text-gray-700">
          {log.title}
        </td>

        <td className="px-4 py-3 text-sm text-gray-700">
          {target || "-"}
        </td>

        <td className="px-4 py-3 text-sm">
          {log.status ? (
            <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700">
              Success
            </span>
          ) : (
            <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-700">
              Failed
            </span>
          )}
        </td>

        <td className="px-4 py-3 text-sm text-gray-500">
          {timeAgo(log.createdAt)}
        </td>
      </tr>
    );
  })}
</tbody>

              </table>

              {paginatedLogs.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-6">
                  No audit logs found
                </p>
              )}
            </>
          )}
        </div>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            {/* Previous */}
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-md border text-sm ${
                currentPage === 1
                  ? "text-gray-400 border-gray-200 cursor-not-allowed"
                  : "text-gray-600 border-gray-300 hover:bg-gray-100"
              }`}
            >
              ‹
            </button>

            {/* Page Numbers */}
            {getPaginationPages(currentPage, totalPages).map((page, index) =>
              page === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  className="px-3 py-2 text-gray-500"
                >
                  …
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => goToPage(page as number)}
                  className={`px-3 py-2 rounded-md border text-sm ${
                    currentPage === page
                      ? "bg-blue-600 text-white border-blue-600"
                      : "text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {page}
                </button>
              )
            )}

            {/* Next */}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-md border text-sm ${
                currentPage === totalPages
                  ? "text-gray-400 border-gray-200 cursor-not-allowed"
                  : "text-gray-600 border-gray-300 hover:bg-gray-100"
              }`}
            >
              ›
            </button>
          </div>
        )}
      </div>
 
  );
};

export default AuditLogs;